import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import AlgoliaMapView from '../../../AlgoliaSearchPage/AlgoliaMapView/AlgoliaMapView';
import { serviceCategoriesListing } from '../../../AlgoliaSearchPage/AlgoliaSearchPage.duck';
import { transformDbCategories } from '../../../../util/data';
import { createResourceLocatorString } from '../../../../util/routes';

import css from './NearbyMap.module.css';

// Start loading the map a little before the section scrolls into view.
const LOAD_MARGIN = '200px';

/**
 * "Near you" on the customer home: the search page's map (services / deals with
 * category pins) in a home section, plus a link to the full map. The map only
 * mounts once the section is about to be seen, so Google Maps and the Algolia
 * query don't load on every home visit.
 */
const NearbyMap = ({ intl, history, routeConfiguration }) => {
  const dispatch = useDispatch();
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [kind, setKind] = useState('service');
  const [categories, setCategories] = useState([]);

  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: LOAD_MARGIN }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Category icons for the pins, loaded with the map.
  useEffect(() => {
    if (!inView) {
      return undefined;
    }
    let cancelled = false;
    dispatch(serviceCategoriesListing())
      .then(data => !cancelled && setCategories(transformDbCategories(data)))
      .catch(error => console.error('Failed to fetch categories for the map:', error));
    return () => {
      cancelled = true;
    };
  }, [inView, dispatch]);

  const openFullMap = () =>
    history.push(
      createResourceLocatorString(
        'AlgoliaSearchPage',
        routeConfiguration,
        { listingType: kind === 'deal' ? 'deals' : 'services' },
        { view: 'map' }
      )
    );

  return (
    <section
      ref={sectionRef}
      className={classNames(css.root, arabicLanguage && css.arabicDirection)}
    >
      <div className={css.headingArea}>
        <div className={css.headingLeft}>
          <div className={css.pill}>
            <span className={css.pinDot} aria-hidden="true" />
            {intl.formatMessage({ id: 'NearbyMap.pill' })}
          </div>
          <h2 className={css.heading}>{intl.formatMessage({ id: 'NearbyMap.heading' })}</h2>
          <p className={css.subheading}>{intl.formatMessage({ id: 'NearbyMap.subheading' })}</p>
        </div>
        {history && routeConfiguration ? (
          <button type="button" className={css.ctaButton} onClick={openFullMap}>
            {intl.formatMessage({ id: 'NearbyMap.openFullMap' })}
          </button>
        ) : null}
      </div>

      {inView ? (
        <AlgoliaMapView
          embedded
          listingType="services"
          categories={categories}
          onKindChange={setKind}
          intl={intl}
        />
      ) : (
        <div className={css.placeholder} data-testid="nearby-map-placeholder" />
      )}
    </section>
  );
};

export default NearbyMap;
